import {
  Customer,
  CustomerForClientPortalView,
  PaymentsClientPortalWhitelistView,
  PaymentsGrowCustomer,
  PaymentsInvoiceEmailMessageView
} from "../../Contracts/Customer";
import { create } from "zustand";
import { useRequestStore } from "../BackendStore";
import { MultiGetResponse, ViewGeneratorKey } from "../../Contracts/Common";
import { Contact } from "../../Contracts/Customer/Contact";
import { useClientPortalMetadataStore } from "../ClientPortalMetadata";
import { AllowedPaymentMethod } from "../../Contracts/Transaction";
import { persist } from "zustand/middleware";
import { useBannerStore } from "../Banner";
import { BannerType } from "../../Contracts/Banner";

const customersUrl = "/v1/payments/customers";
const growCustomersUrl = "/v1/payments/customers/unmapped";
const mapGrowCustomerUrl = "/v1/payments/customers/map";
const unmapGrowCustomerUrl = "/v1/payments/customers/unmap/<customer_id>";
const customersForClientPortalUrl = "/v1/payments/client-portal/customers";
const customerDetailsUrl = "/v1/payments/customers/<customer_id>";
const editCustomerDetailsUrl = "/v1/payments/customers/<customer_id>";
const requestCustomerAddPaymentMethodUrl =
  "/v1/payments/customers/<customer_id>/requestPaymentMethod/";

const editWhiteListUrl = "/v1/payments/whitelists/<whitelist_id>";
const createWhiteListUrl = "/v1/payments/whitelists";
const deleteWhiteListUrl = "/v1/payments/whitelists/<whitelist_id>";

const editContactUrl = "/v1/payments/contacts/<contact_id>";
const createContactUrl = "/v1/payments/contacts";
const deleteContactUrl = "/v1/payments/contacts/<contact_id>";

const getEmailHistoryUrl = "/v1/payments/email/history";

const updateAllowedPaymentMethodsUrl = "/v1/payments/customers/payment-methods";
const setPaymentMethodAsPrimaryUrl =
  "/v1/payments/customer/{customer_id}/paymentMethod/{payment_method_id}/makePrimary/";
const removePaymentMethodUrl =
  "/v1/payments/customer/{customer_id}/paymentMethod/{payment_method_id}/";

interface CustomerState {
  isLoading: boolean;
  customers: Customer[];
  growCustomers: PaymentsGrowCustomer[];
  growCustomersCount?: number;
  customersCount?: number;
  getCustomers: (
    getAccessTokenSilently: any,
    limit?: number,
    sort?: string,
    page?: number,
    filters?: string,
    query?: string
  ) => Promise<void>;
  getGrowCustomers: (
    getAccessTokenSilently: any,
    limit?: number,
    sort?: string,
    page?: number,
    filters?: string,
    query?: string
  ) => Promise<void>;
  mapGrowCustomer: (
    getAccessTokenSilently: any,
    growCustomerId: number,
    collectibleInvoiceCustomerId: number
  ) => Promise<void>;
  unmapGrowCustomer: (getAccessTokenSilently: any, customerId: number) => Promise<void>;
  requestCustomerToAddPaymentMethod: (
    getAccessTokenSilently: any,
    customerId: string
  ) => Promise<void>;

  // Client Portal
  clientPortalCustomers: CustomerForClientPortalView[];
  clientPortalCustomersCount?: number;
  getClientPortalCustomers: (limit?: number, sort?: string, page?: number) => Promise<void>;

  // customer details actions
  getCustomerDetails: (
    getAccessTokenSilently: any,
    customerId: string,
    withLoading?: boolean
  ) => Promise<void>;
  editCustomerDetails: (
    getAccessTokenSilently: any,
    customerId: string,
    request: {
      reminder_enabled_date?: Date | null;
      auto_collection_enabled_date?: Date | null;
    }
  ) => Promise<void>;
  customerDetailEntity?: Customer;

  // whitelist actions
  editWhiteList: (
    getAccessTokenSilently: any,
    whitelistId: number,
    emailOrDomain: string
  ) => Promise<void>;
  createWhiteList: (
    getAccessTokenSilently: any,
    customerId: number,
    emailOrDomain: string
  ) => Promise<void>;
  deleteWhiteList: (getAccessTokenSilently: any, whitelistId: number) => Promise<void>;

  // contacts actions
  editContact: (
    getAccessTokenSilently: any,
    contactId: number,
    name: string,
    email: string[]
  ) => Promise<void>;
  createContact: (
    getAccessTokenSilently: any,
    customerId: number,
    name: string,
    email: string[]
  ) => Promise<void>;
  deleteContact: (getAccessTokenSilently: any, contactId: number) => Promise<void>;

  // email history
  emailHistory: PaymentsInvoiceEmailMessageView[];
  emailHistoryCount?: number;
  getEmailHistory: (
    getAccessTokenSilently: any,
    customerId: string,
    limit?: number,
    sort?: string,
    page?: number
  ) => Promise<void>;

  // payment methods
  updateAllowedPaymentMethods: (
    getAccessTokenSilently: any,
    allowedPaymentMethods: AllowedPaymentMethod[]
  ) => Promise<void>;
  removePaymentMethod: (
    getAccessTokenSilently: any,
    paymentMethodId: number,
    customerId: string
  ) => Promise<void>;
  setPaymentMethodAsPrimary: (
    getAccessTokenSilently: any,
    paymentMethodId: number,
    customerId: string
  ) => Promise<void>;
}

export const useCustomerStore = create<CustomerState>()(
  persist(
    (set, get) => {
      const { getRequest, getRequestNoAuth, putRequest, postRequest, deleteRequest } =
        useRequestStore.getState();
      const { addBanner } = useBannerStore.getState();

      const getCustomers = async (
        getAccessTokenSilently: any,
        limit?: number,
        sort?: string,
        page?: number,
        filters?: string,
        query?: string
      ) => {
        set({ isLoading: true });
        try {
          let requestParams: {
            limit: number | undefined;
            sort: string | undefined;
            include_total_count: boolean;
            page: number | undefined;
            filters: string | undefined;
            q: string | undefined;
          } = {
            limit,
            sort,
            include_total_count: true,
            page,
            filters,
            q: undefined
          };

          if (query && query.length >= 3) {
            requestParams.q = query;
          }

          const response = await getRequest<MultiGetResponse<Customer>>(
            getAccessTokenSilently,
            customersUrl,
            requestParams
          );
          set({
            customers: response.data,
            customersCount: response?.filtered_count_map?.all,
            isLoading: false
          });
        } catch (error) {
          console.error("Error fetching invoices", error);
          set({ isLoading: false });
        }
      };

      const requestCustomerToAddPaymentMethod = async (
        getAccessTokenSilently: any,
        customerId: string
      ) => {
        try {
          await postRequest(
            getAccessTokenSilently,
            requestCustomerAddPaymentMethodUrl.replace("<customer_id>", customerId),
            {}
          );
        } catch (error) {
          console.error("Error requesting payment method", error);
        }
      };

      const getGrowCustomers = async (
        getAccessTokenSilently: any,
        limit?: number,
        sort?: string,
        page?: number,
        filters?: string,
        query?: string
      ) => {
        set({ isLoading: true });
        try {
          let requestParams: {
            limit: number | undefined;
            sort: string | undefined;
            include_total_count: boolean;
            page: number | undefined;
            filters: string | undefined;
            q: string | undefined;
          } = {
            limit,
            sort,
            include_total_count: true,
            page,
            filters,
            q: undefined
          };

          if (query && query.length >= 3) {
            requestParams.q = query;
          }

          const response = await getRequest<MultiGetResponse<PaymentsGrowCustomer>>(
            getAccessTokenSilently,
            growCustomersUrl,
            requestParams
          );
          set({
            growCustomers: response.data,
            growCustomersCount: response?.filtered_count_map?.all,
            isLoading: false
          });
        } catch (error) {
          console.error("Error fetching invoices", error);
          set({ isLoading: false });
        }
      };

      const mapGrowCustomer = async (
        getAccessTokenSilently: any,
        growCustomerId: number,
        collectibleInvoiceCustomerId: number
      ) => {
        set({ isLoading: true });
        try {
          await postRequest<
            {
              grow_customer_id: number;
              collectible_invoice_customer_id: number;
            },
            PaymentsGrowCustomer
          >(getAccessTokenSilently, mapGrowCustomerUrl, {
            grow_customer_id: growCustomerId,
            collectible_invoice_customer_id: collectibleInvoiceCustomerId
          });

          const growCustomers = get().growCustomers;
          const updatedGrowCustomers = growCustomers.filter(
            customer => customer.id !== growCustomerId
          );
          addBanner({
            type: BannerType.Success,
            message: "Customer mapped successfully"
          });
          set({
            growCustomers: updatedGrowCustomers,
            isLoading: false
          });
        } catch (error) {
          console.error("Error mapping grow customer", error);
          set({ isLoading: false });
        }
      };

      const unmapGrowCustomer = async (getAccessTokenSilently: any, customerId: number) => {
        set({ isLoading: true });
        try {
          const patchedGrowCustomer = await getRequest<PaymentsGrowCustomer>(
            getAccessTokenSilently,
            unmapGrowCustomerUrl.replace("<customer_id>", customerId.toString())
          );
          const growCustomers = get().growCustomers;
          const updatedGrowCustomers = growCustomers.map(customer =>
            customer.id === customerId ? patchedGrowCustomer : customer
          );
          addBanner({
            type: BannerType.Success,
            message: "Customer unmapped successfully"
          });
          set({
            growCustomers: updatedGrowCustomers,
            isLoading: false
          });
        } catch (error) {
          console.error("Error unmapping grow customer", error);
          set({ isLoading: false });
        }
      };

      const getClientPortalCustomers = async (
        limit?: number,
        sort?: string,
        page?: number,
        filters?: string
      ) => {
        set({ isLoading: true });
        const { clientPortalToken } = useClientPortalMetadataStore.getState();
        try {
          const response = await getRequestNoAuth<MultiGetResponse<CustomerForClientPortalView>>(
            customersForClientPortalUrl,
            {
              token: clientPortalToken,
              limit,
              sort,
              include_total_count: true,
              page,
              filters
            }
          );
          set({
            clientPortalCustomers: response.data,
            clientPortalCustomersCount: response?.filtered_count_map?.all,
            isLoading: false
          });
        } catch (error) {
          console.error("Error fetching invoices", error);
          set({ isLoading: false });
        }
      };

      const getCustomerDetails = async (
        getAccessTokenSilently: any,
        customerId: string,
        withLoading: boolean = true
      ) => {
        if (withLoading) set({ isLoading: true });
        try {
          const response = await getRequest<Customer>(
            getAccessTokenSilently,
            customerDetailsUrl.replace("<customer_id>", customerId),
            {
              included_child_entities: `${ViewGeneratorKey.AllContacts},${ViewGeneratorKey.AvailablePaymentMethods},${ViewGeneratorKey.AllowedPaymentMethods},${ViewGeneratorKey.AllWhitelists},${ViewGeneratorKey.Country}`
            }
          );
          set({
            customerDetailEntity: response,
            isLoading: false
          });
        } catch (error) {
          console.error("Error fetching invoices", error);
          set({ isLoading: false });
        }
      };

      const editCustomerDetails = async (
        getAccessTokenSilently: any,
        customerId: string,
        request: {
          reminder_enabled_date?: Date | null;
          auto_collection_enabled_date?: Date | null;
        }
      ) => {
        try {
          const response = await putRequest<
            {
              reminder_enabled_date?: Date | null;
              auto_collection_enabled_date?: Date | null;
            },
            Customer
          >(
            getAccessTokenSilently,
            editCustomerDetailsUrl.replace("<customer_id>", customerId),
            request,
            {
              included_child_entities: `${ViewGeneratorKey.AllContacts},${ViewGeneratorKey.AvailablePaymentMethods},${ViewGeneratorKey.AllowedPaymentMethods},${ViewGeneratorKey.AllWhitelists},${ViewGeneratorKey.Country}`
            }
          );
          const currentCustomers = get().customers;
          if (currentCustomers && currentCustomers.length) {
            const updatedCustomers = currentCustomers.map(c =>
              c.id === response.id ? response : c
            );
            set({ customerDetailEntity: response, customers: updatedCustomers });
          } else {
            set({ customerDetailEntity: response });
          }
        } catch (error) {
          console.error("Error editing customer details", error);
          return Promise.reject(error.response.data?.description?.replace("Bad Request: ", ""));
        }
      };

      const editWhiteList = async (
        getAccessTokenSilently: any,
        whitelistId: number,
        emailOrDomain: string
      ) => {
        try {
          const response = await putRequest<
            { email_or_domain: string },
            PaymentsClientPortalWhitelistView
          >(
            getAccessTokenSilently,
            editWhiteListUrl.replace("<whitelist_id>", whitelistId.toString()),
            { email_or_domain: emailOrDomain }
          );
          const currentCustomerDetailEntity = get().customerDetailEntity;
          if (
            currentCustomerDetailEntity &&
            currentCustomerDetailEntity.id !== undefined &&
            currentCustomerDetailEntity.whitelists?.data
          ) {
            const updatedCustomerDetailEntity = {
              ...currentCustomerDetailEntity,
              whitelists: {
                data: currentCustomerDetailEntity.whitelists.data.map(whitelist =>
                  whitelist.id === whitelistId
                    ? {
                        ...whitelist,
                        ...response
                      }
                    : whitelist
                )
              }
            };
            addBanner({
              type: BannerType.Success,
              message: "Client portal access updated successfully"
            });
            set({
              customerDetailEntity: updatedCustomerDetailEntity
            });
          }
        } catch (error) {
          console.error("Error editing whitelist", error);
        }
      };

      const createWhiteList = async (
        getAccessTokenSilently: any,
        customerId: number,
        emailOrDomain: string
      ) => {
        try {
          const response = await postRequest<
            { email_or_domain: string; customer_id: number },
            PaymentsClientPortalWhitelistView
          >(getAccessTokenSilently, createWhiteListUrl, {
            email_or_domain: emailOrDomain,
            customer_id: customerId
          });
          const currentCustomerDetailEntity = get().customerDetailEntity;
          if (
            currentCustomerDetailEntity &&
            currentCustomerDetailEntity.id !== undefined &&
            currentCustomerDetailEntity.whitelists?.data
          ) {
            const updatedCustomerDetailEntity = {
              ...currentCustomerDetailEntity,
              whitelists: {
                data: [response, ...currentCustomerDetailEntity.whitelists.data]
              }
            };
            addBanner({
              type: BannerType.Success,
              message: "Client portal access created successfully"
            });
            set({
              customerDetailEntity: updatedCustomerDetailEntity
            });
          }
        } catch (error) {
          console.error("Error creating whitelist", error);
        }
      };

      const deleteWhiteList = async (getAccessTokenSilently: any, whitelistId: number) => {
        try {
          await deleteRequest(
            getAccessTokenSilently,
            deleteWhiteListUrl.replace("<whitelist_id>", whitelistId.toString())
          );
          const currentCustomerDetailEntity = get().customerDetailEntity;
          if (
            currentCustomerDetailEntity &&
            currentCustomerDetailEntity.id !== undefined &&
            currentCustomerDetailEntity.whitelists?.data
          ) {
            const updatedCustomerDetailEntity = {
              ...currentCustomerDetailEntity,
              whitelists: {
                data: currentCustomerDetailEntity.whitelists.data.filter(
                  whitelist => whitelist.id !== whitelistId
                )
              }
            };
            addBanner({
              type: BannerType.Success,
              message: "Client portal access deleted successfully"
            });
            set({
              customerDetailEntity: updatedCustomerDetailEntity
            });
          }
        } catch (error) {
          console.error("Error deleting whitelist", error);
        }
      };

      const editContact = async (
        getAccessTokenSilently: any,
        contactId: number,
        name: string,
        email: string[]
      ) => {
        try {
          const response = await putRequest<{ name: string; email: string[] }, Contact>(
            getAccessTokenSilently,
            editContactUrl.replace("<contact_id>", contactId.toString()),
            { name, email }
          );
          const currentCustomerDetailEntity = get().customerDetailEntity;
          if (
            currentCustomerDetailEntity &&
            currentCustomerDetailEntity.id !== undefined &&
            currentCustomerDetailEntity.contacts?.data
          ) {
            const updatedCustomerDetailEntity = {
              ...currentCustomerDetailEntity,
              contacts: {
                data: currentCustomerDetailEntity.contacts.data.map(contact =>
                  contact.id === contactId
                    ? {
                        ...contact,
                        ...response
                      }
                    : contact
                )
              }
            };
            addBanner({
              type: BannerType.Success,
              message: "Contact updated successfully"
            });
            set({
              customerDetailEntity: updatedCustomerDetailEntity
            });
          }
        } catch (error) {
          console.error("Error editing contact", error);
        }
      };

      const createContact = async (
        getAccessTokenSilently: any,
        customerId: number,
        name: string,
        email: string[]
      ) => {
        try {
          const response = await postRequest<
            { name: string; email: string[]; customer_id: number },
            Contact
          >(getAccessTokenSilently, createContactUrl, {
            name,
            email,
            customer_id: customerId
          });
          const currentCustomerDetailEntity = get().customerDetailEntity;
          if (
            currentCustomerDetailEntity &&
            currentCustomerDetailEntity.id !== undefined &&
            currentCustomerDetailEntity.contacts?.data
          ) {
            const updatedCustomerDetailEntity = {
              ...currentCustomerDetailEntity,
              contacts: {
                data: [response, ...currentCustomerDetailEntity.contacts.data]
              }
            };
            addBanner({
              type: BannerType.Success,
              message: "Contact created successfully"
            });
            set({
              customerDetailEntity: updatedCustomerDetailEntity
            });
          }
        } catch (error) {
          console.error("Error creating contact", error);
        }
      };

      const deleteContact = async (getAccessTokenSilently: any, contactId: number) => {
        try {
          await deleteRequest(
            getAccessTokenSilently,
            deleteContactUrl.replace("<contact_id>", contactId.toString())
          );
          const currentCustomerDetailEntity = get().customerDetailEntity;
          if (
            currentCustomerDetailEntity &&
            currentCustomerDetailEntity.id !== undefined &&
            currentCustomerDetailEntity.contacts?.data
          ) {
            const updatedCustomerDetailEntity = {
              ...currentCustomerDetailEntity,
              contacts: {
                data: currentCustomerDetailEntity.contacts.data.filter(
                  contact => contact.id !== contactId
                )
              }
            };
            addBanner({
              type: BannerType.Success,
              message: "Contact deleted successfully"
            });
            set({
              customerDetailEntity: updatedCustomerDetailEntity
            });
          }
        } catch (error) {
          console.error("Error deleting contact", error);
        }
      };

      const getEmailHistory = async (
        getAccessTokenSilently: any,
        customerId: string,
        limit?: number,
        sort?: string,
        page?: number
      ) => {
        try {
          const response = await getRequest<MultiGetResponse<PaymentsInvoiceEmailMessageView>>(
            getAccessTokenSilently,
            getEmailHistoryUrl,
            {
              limit,
              sort,
              include_total_count: true,
              page,
              filters: `customer_id:eq:${customerId}`
            }
          );
          set({
            emailHistory: response.data,
            emailHistoryCount: response?.filtered_count_map?.all
          });
        } catch (error) {
          console.error("Error fetching email history", error);
        }
      };

      const updateAllowedPaymentMethods = async (
        getAccessTokenSilently: any,
        allowedPaymentMethods: AllowedPaymentMethod[]
      ) => {
        try {
          const response = await putRequest<
            AllowedPaymentMethod[],
            MultiGetResponse<AllowedPaymentMethod>
          >(getAccessTokenSilently, updateAllowedPaymentMethodsUrl, allowedPaymentMethods);
          const existingCustomerDetailEntity = get().customerDetailEntity;
          if (existingCustomerDetailEntity && existingCustomerDetailEntity.id !== undefined) {
            const updatedCustomerDetailEntity = {
              ...existingCustomerDetailEntity,
              allowed_payment_methods: {
                data: response.data
              }
            };
            addBanner({
              type: BannerType.Success,
              message: "Payment methods updated successfully"
            });
            set({
              customerDetailEntity: updatedCustomerDetailEntity
            });
          }
        } catch (error) {
          console.error("Error updating allowed payment methods", error);
        }
      };

      const removePaymentMethod = async (
        getAccessTokenSilently: any,
        paymentMethodId: number,
        customerId: string
      ) => {
        try {
          await deleteRequest(
            getAccessTokenSilently,
            removePaymentMethodUrl
              .replace("{customer_id}", customerId)
              .replace("{payment_method_id}", paymentMethodId.toString())
          );
          await getCustomerDetails(getAccessTokenSilently, customerId, false);
          addBanner({
            type: BannerType.Success,
            message: "Payment method removed successfully"
          });
        } catch (error) {
          console.error("Error removing payment method", error);
        }
      };

      const setPaymentMethodAsPrimary = async (
        getAccessTokenSilently: any,
        paymentMethodId: number,
        customerId: string
      ) => {
        try {
          await putRequest(
            getAccessTokenSilently,
            setPaymentMethodAsPrimaryUrl
              .replace("{customer_id}", customerId)
              .replace("{payment_method_id}", paymentMethodId.toString()),
            {}
          );
          await getCustomerDetails(getAccessTokenSilently, customerId, false);
          addBanner({
            type: BannerType.Success,
            message: "Payment method set as primary successfully"
          });
        } catch (error) {
          console.error("Error setting payment method as primary", error);
        }
      };

      return {
        isLoading: true,
        customers: [],
        growCustomers: [],
        clientPortalCustomers: [],
        emailHistory: [],
        getCustomers,
        getGrowCustomers,
        mapGrowCustomer,
        unmapGrowCustomer,
        getCustomerDetails,
        editCustomerDetails,
        requestCustomerToAddPaymentMethod,
        getClientPortalCustomers,
        editWhiteList,
        deleteWhiteList,
        createWhiteList,
        editContact,
        createContact,
        deleteContact,
        getEmailHistory,
        updateAllowedPaymentMethods,
        removePaymentMethod,
        setPaymentMethodAsPrimary
      };
    },
    {
      name: "customerStore",
      partialize: state => ({
        clientPortalCustomersCount: state.clientPortalCustomersCount,
        clientPortalCustomers: state.clientPortalCustomers
      })
    }
  )
);
